import { useState } from "react";

const courseOptions = [
  "AI & Machine Learning Fundamentals",
  "Advanced Cybersecurity Bootcamp",
  "Cloud Architecture Masterclass",
  "Data Analytics with Python & SQL",
  "Digital Marketing Pro Certification",
  "Full-Stack App Development Bootcamp",
  "Not sure yet, need guidance",
];
const timeSlots = ["Morning (9am–12pm CST)", "Afternoon (12pm–4pm CST)", "Evening (4pm–7pm CST)"];
const goals = ["Career change into tech", "Get promoted / upskill", "Build a personal project", "Grow my business", "Academic / research", "Other"];

type FormData = { name: string; email: string; phone: string; course: string; goal: string; timeSlot: string; message: string; };
const initial: FormData = { name: "", email: "", phone: "", course: "", goal: "", timeSlot: "", message: "" };

export default function ConsultationForm() {
  const [form, setForm] = useState<FormData>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) { setError("Message must be 500 characters or less."); return; }
    setError(""); setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d79ubtr2hsar0k5m0tr0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ ...form }).toString(),
      });
      setSubmitted(true);
    } catch (_e) { setError("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="py-20 bg-slate-50" id="consultation">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 border border-[#C8F135]/40 rounded-full px-4 py-1.5 mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{color: "#9bc400"}}>Free 15-Min Call</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            Book a Free <span style={{color: "#9bc400"}}>Consultation</span>
          </h2>
          <p className="text-gray-500 text-sm">Not sure which course is right for you? Our advisors will help you find the perfect learning path, no pressure.</p>
        </div>

        {/* What to expect */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: "ri-time-line", title: "15 Minutes", desc: "Quick, focused call with a real advisor" },
            { icon: "ri-road-map-line", title: "Custom Path", desc: "Personalized course recommendation for your goals" },
            { icon: "ri-gift-line", title: "Free Bonus", desc: "Get a 10% discount code just for booking" },
          ].map((item) => (
            <div key={item.title} className="bg-white rounded-xl p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8F135]/20 mx-auto mb-3">
                <i className={`${item.icon} text-lg`} style={{color: "#9bc400"}} />
              </div>
              <div className="text-sm font-bold text-gray-900 mb-1">{item.title}</div>
              <div className="text-xs text-gray-500">{item.desc}</div>
            </div>
          ))}
        </div>

        <div className="bg-black rounded-2xl p-8">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#C8F135]/20 mb-4">
                <i className="ri-check-line text-[#C8F135] text-2xl" />
              </div>
              <h3 className="text-xl font-black text-white mb-2">Consultation Booked!</h3>
              <p className="text-gray-400 text-sm mb-3">
                We&apos;ll confirm your slot and send a calendar invite to <strong className="text-white">{form.email}</strong> within a few hours.
              </p>
              <div className="bg-[#C8F135]/10 border border-[#C8F135]/20 rounded-xl px-5 py-3 text-center">
                <p className="text-[#C8F135] text-sm font-semibold">
                  <i className="ri-gift-line mr-1" /> Your 10% discount code: <strong>CONSULT10</strong>
                </p>
              </div>
            </div>
          ) : (
            <form data-readdy-form id="academy-consultation-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Phone</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Course You&apos;re Interested In</label>
                <select name="course" value={form.course} onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                  <option value="" className="bg-gray-900">Select a course (optional)</option>
                  {courseOptions.map((o) => <option key={o} value={o} className="bg-gray-900">{o}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Your Main Goal *</label>
                  <select name="goal" value={form.goal} onChange={handleChange} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                    <option value="" className="bg-gray-900">What&apos;s your goal?</option>
                    {goals.map((g) => <option key={g} value={g} className="bg-gray-900">{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Preferred Time Slot *</label>
                  <select name="timeSlot" value={form.timeSlot} onChange={handleChange} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                    <option value="" className="bg-gray-900">Select time slot</option>
                    {timeSlots.map((t) => <option key={t} value={t} className="bg-gray-900">{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">
                  Anything else we should know? <span className="text-gray-600">({form.message.length}/500)</span>
                </label>
                <textarea name="message" value={form.message} onChange={handleChange} maxLength={500} rows={3}
                  placeholder="Background, specific questions, or anything that would help us prepare..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C8F135] transition-colors resize-none" />
              </div>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-4 rounded-xl bg-[#C8F135] text-black font-bold text-sm cursor-pointer hover:bg-[#b5e000] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin" /> Booking...</> : <><i className="ri-calendar-check-line" /> Book My Free Consultation</>}
              </button>
              <p className="text-xs text-gray-600 text-center">Free, no-obligation call. We&apos;ll never spam you.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
